import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Calendar, Clock, ArrowLeft, ArrowRight, Tag, List } from 'lucide-react';
import { posts, getPost, formatDate } from '../data/blogIndex';

const ORIGIN = 'https://www.sacvpn.com';

// Stable slug for a heading so the TOC anchor matches react-markdown output.
function slugify(text) {
  return String(text)
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

// Extract plain text from a react node (heading children can be arrays/elements).
function nodeText(children) {
  if (children == null) return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(nodeText).join('');
  if (children.props && children.props.children) return nodeText(children.props.children);
  return '';
}

// Markdown renderers styled to the dark / brand-blue prose palette.
const components = {
  h1: () => null, // the article H1 is rendered in the page header, not the body
  h2: ({ children }) => {
    const id = slugify(nodeText(children));
    return (
      <h2 id={id} className="scroll-mt-28 text-2xl md:text-3xl font-display font-bold text-white mt-14 mb-5 tracking-tight">
        {children}
      </h2>
    );
  },
  h3: ({ children }) => (
    <h3 className="text-xl font-semibold text-white mt-9 mb-3">{children}</h3>
  ),
  p: ({ children }) => <p className="text-gray-300 text-[1.05rem] leading-[1.8] mb-6">{children}</p>,
  a: ({ href, children }) => (
    <a href={href} className="text-brand-400 underline decoration-brand-500/40 underline-offset-2 hover:text-brand-300 transition-colors">
      {children}
    </a>
  ),
  ul: ({ children }) => <ul className="mb-6 space-y-2.5 pl-1">{children}</ul>,
  ol: ({ children }) => <ol className="mb-6 space-y-2.5 pl-1 list-none [counter-reset:item]">{children}</ol>,
  li: ({ children, ordered }) =>
    ordered ? (
      <li className="relative pl-9 text-gray-300 text-[1.05rem] leading-[1.7] [counter-increment:item] before:content-[counter(item)] before:absolute before:left-0 before:top-0.5 before:flex before:items-center before:justify-center before:w-6 before:h-6 before:rounded-md before:bg-brand-500/15 before:border before:border-brand-500/25 before:text-brand-300 before:text-xs before:font-mono">
        {children}
      </li>
    ) : (
      <li className="relative pl-6 text-gray-300 text-[1.05rem] leading-[1.7] before:content-[''] before:absolute before:left-1 before:top-[0.7rem] before:w-1.5 before:h-1.5 before:rounded-full before:bg-brand-400">
        {children}
      </li>
    ),
  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
  em: ({ children }) => <em className="text-gray-400 not-italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="my-8 rounded-xl border border-brand-500/20 bg-brand-500/[0.06] px-6 py-4 text-gray-200 [&>p]:mb-0 [&>p]:text-brand-50/90 [&>p]:leading-relaxed border-l-2 border-l-brand-400">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-12 border-white/10" />,
  img: ({ src, alt }) => (
    <img
      src={src}
      alt={alt || ''}
      loading="lazy"
      className="my-9 w-full rounded-xl border border-white/[0.08]"
    />
  ),
  code: ({ inline, children }) =>
    inline ? (
      <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-[0.9em] text-brand-200">{children}</code>
    ) : (
      <code className="font-mono text-sm text-gray-200">{children}</code>
    ),
  pre: ({ children }) => (
    <pre className="my-8 overflow-x-auto rounded-xl border border-white/[0.08] bg-dark-800 p-5 text-sm leading-relaxed">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-8 overflow-x-auto rounded-xl border border-white/[0.08]">
      <table className="w-full text-left text-sm text-gray-300">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border-b border-white/10 px-4 py-3 font-semibold text-white">{children}</th>,
  td: ({ children }) => <td className="border-b border-white/[0.06] px-4 py-3">{children}</td>,
};

function useMarkdown(slug) {
  const [state, setState] = useState({ status: 'loading', body: '' });
  useEffect(() => {
    let active = true;
    setState({ status: 'loading', body: '' });
    fetch(`/data/blog/${slug}.md`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (active) setState({ status: 'ready', body: text });
      })
      .catch(() => {
        if (active) setState({ status: 'error', body: '' });
      });
    return () => {
      active = false;
    };
  }, [slug]);
  return state;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  const { status, body } = useMarkdown(slug);
  const [activeId, setActiveId] = useState('');
  const articleRef = useRef(null);

  // Strip the hero image line + H1 + byline from the body; those render in the
  // page header. Everything from the first "---" onward is the article proper.
  const article = useMemo(() => {
    if (!body) return '';
    const idx = body.indexOf('\n---');
    return idx === -1 ? body : body.slice(idx + 4).trim();
  }, [body]);

  // Table of contents from the H2 headings in the article body.
  const toc = useMemo(() => {
    const out = [];
    const re = /^##\s+(.+?)\s*$/gm;
    let m;
    while ((m = re.exec(article)) !== null) {
      const text = m[1].replace(/[*_`]/g, '');
      out.push({ text, id: slugify(text) });
    }
    return out;
  }, [article]);

  const readMinutes = useMemo(() => {
    const words = article.replace(/[#>*`_-]/g, ' ').split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(words / 200));
  }, [article]);

  // Scroll-spy: highlight the TOC entry for the section currently in view.
  useEffect(() => {
    if (status !== 'ready' || !articleRef.current) return;
    const headings = Array.from(articleRef.current.querySelectorAll('h2[id]'));
    if (headings.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 }
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [status, article]);

  if (!post) return <Navigate to="/blog" replace />;

  const canonical = `${ORIGIN}/blog/${post.slug}`;
  const absImage = `${ORIGIN}${post.image}`;
  const keywords = (post.tags || []).join(', ');

  const related = posts
    .filter((p) => p.slug !== post.slug && (p.tags || []).some((t) => (post.tags || []).includes(t)))
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: [absImage],
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Organization',
      name: 'SACVPN',
      url: ORIGIN,
    },
    publisher: {
      '@type': 'Organization',
      name: 'SACVPN',
      logo: {
        '@type': 'ImageObject',
        url: `${ORIGIN}/sacvpn-logo.svg`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonical,
    },
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | SACVPN</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={canonical} />

        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={absImage} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:publisher" content="SACVPN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={absImage} />

        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-dark-900">
        {/* Header */}
        <header className="relative overflow-hidden border-b border-white/5">
          <div className="absolute inset-0 bg-[radial-gradient(800px_380px_at_78%_-10%,rgba(14,165,233,0.12),transparent_60%)]" />
          <div className="relative z-10 max-w-4xl mx-auto px-6 pt-12 pb-10">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to blog
            </Link>

            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">{post.tags?.[0]}</p>

            <h1 className="text-3xl md:text-5xl font-display font-bold text-white tracking-tight leading-[1.1]">
              {post.title}
            </h1>

            <p className="mt-5 text-lg text-gray-400 leading-relaxed max-w-2xl">{post.excerpt}</p>

            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-gray-500">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-brand-400" />
                {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-brand-400" />
                {readMinutes} min read
              </span>
              <span className="text-gray-600">By SACVPN</span>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {(post.tags || []).map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-gray-300"
                >
                  <Tag className="w-3 h-3 text-brand-400" />
                  {t}
                </span>
              ))}
            </div>
          </div>
        </header>

        {/* Hero image */}
        <div className="max-w-5xl mx-auto px-6 -mt-2 pt-10">
          <img
            src={post.image}
            alt={post.title}
            width="1200"
            height="675"
            className="w-full rounded-2xl border border-white/[0.08] object-cover"
          />
        </div>

        {/* Body + sidebar */}
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="grid lg:grid-cols-[1fr_260px] gap-12">
            <article ref={articleRef} className="min-w-0">
              {status === 'loading' && (
                <div className="animate-pulse space-y-4">
                  {[3, 5, 4, 5, 3].map((w, i) => (
                    <div key={i} className="h-4 bg-white/5 rounded" style={{ width: `${w * 18}%` }} />
                  ))}
                </div>
              )}
              {status === 'error' && (
                <p className="text-gray-400">This article could not be loaded. Please try again shortly.</p>
              )}
              {status === 'ready' && (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                  {article}
                </ReactMarkdown>
              )}

              {/* Inline CTA */}
              <div className="mt-14 rounded-2xl border border-brand-500/20 bg-[radial-gradient(600px_240px_at_50%_-20%,rgba(14,165,233,0.16),transparent_70%)] bg-dark-800 px-8 py-10 text-center">
                <h2 className="text-2xl font-display font-bold text-white tracking-tight mb-3">
                  Ready to protect your connection?
                </h2>
                <p className="text-gray-400 max-w-xl mx-auto mb-7">
                  Join SACVPN for fast, private, WireGuard-powered internet access. Start a 14-day free trial, no credit card required.
                </p>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
                >
                  View pricing <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-28 space-y-6">
                {toc.length > 0 && (
                  <nav className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-5">
                    <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-gray-500 mb-4">
                      <List className="w-3.5 h-3.5" />
                      On this page
                    </p>
                    <ul className="space-y-2.5">
                      {toc.map((item) => (
                        <li key={item.id}>
                          <a
                            href={`#${item.id}`}
                            className={`block text-sm leading-snug transition-colors ${
                              activeId === item.id
                                ? 'text-brand-300 font-medium'
                                : 'text-gray-400 hover:text-white'
                            }`}
                          >
                            {item.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                )}

                <div className="rounded-xl border border-brand-500/20 bg-dark-800/60 p-5">
                  <p className="text-xs font-mono uppercase tracking-[0.18em] text-brand-400 mb-2">SACVPN</p>
                  <p className="text-sm text-gray-300 leading-relaxed mb-4">
                    A private WireGuard network for your team and your games. No-logs, unlimited devices.
                  </p>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-400 hover:text-brand-300 transition-colors"
                  >
                    Start free trial <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>

        {/* Related posts */}
        {related.length > 0 && (
          <section className="border-t border-white/5">
            <div className="max-w-7xl mx-auto px-6 py-16">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-6">Related articles</p>
              <div className="grid md:grid-cols-3 gap-6">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    className="group flex flex-col overflow-hidden rounded-xl border border-white/[0.08] bg-dark-800/40 hover:border-brand-500/30 transition-colors"
                  >
                    <div className="overflow-hidden bg-dark-800">
                      <img
                        src={r.image}
                        alt={r.title}
                        loading="lazy"
                        width="1200"
                        height="675"
                        className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="p-5">
                      <span className="text-xs font-mono uppercase tracking-[0.16em] text-brand-400">{r.tags?.[0]}</span>
                      <h3 className="mt-2 text-base font-semibold text-white leading-snug group-hover:text-brand-300 transition-colors line-clamp-2">
                        {r.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
